import { supabase, unwrap, fetchAllRows, deleteRecordingFiles, apiPost } from '../supabase';
import { resolveRange, type DateRangeValue } from '../dates';
import { parseLabels } from '../labels';
import { phoneQueryDigits } from '../phone';
import { archiveQueryGroup, type ArchiveVisibility } from '../archive-filter';
import { safeTerm, exportCalls, type Sort } from './calls';
import type { Contact, ContactTab, Paged, TabCounts } from '../types';

export interface ContactQuery {
  activity?: boolean;
  range: DateRangeValue;
  tab: ContactTab;
  employeeId?: string;
  search?: string;
  archived?: ArchiveVisibility;
  connection?: 'connected' | 'never';
  notes?: 'with' | 'without';
  reviewed?: 'all_reviewed' | 'pending';
  recordings?: 'with' | 'without';
  talkTime?: 'short' | 'medium' | 'long';
  frequency?: 'frequent' | 'rare';
  lastCallType?: 'incoming' | 'outgoing' | 'missed';
  label?: string;
}

export type ContactSortKey = 'last_call_time' | 'name' | 'total_calls' | 'total_duration';

const toNum = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const toStr = (v: unknown) => (v === null || v === undefined || v === '' ? null : String(v));

export function mapContact(row: Record<string, unknown>): Contact {
  return {
    phone: String(row.phone ?? ''),
    name: toStr(row.display_name ?? row.name),
    note: toStr(row.display_note ?? row.note),
    labels: parseLabels(row.display_labels ?? row.label ?? row.labels),
    isArchived: Boolean(row.is_archived),
    isStarred: Boolean(row.is_starred),
    identityId: row.identity_id ? toNum(row.identity_id) : null,
    lastCallTime: toStr(row.last_call_time ?? row.last_call),
    lastCallType: toStr(row.last_call_type)?.toLowerCase() ?? null,
    lastCallDuration: toNum(row.last_call_duration),
    totalCalls: toNum(row.total_calls),
    totalConnected: toNum(row.connected_calls ?? row.total_connected),
    totalIncoming: toNum(row.total_incoming),
    totalOutgoing: toNum(row.total_outgoing),
    totalMissed: toNum(row.missed_calls ?? row.total_missed ?? row.total_not_answered),
    totalDuration: toNum(row.total_duration),
    totalRecordings: toNum(row.recordings_count ?? row.total_recordings),
    unreviewedCount: toNum(row.unreviewed_count),
    lastRecordingUrl: toStr(row.last_call_rec_url)?.trim() || null,
    createdAt: toStr(row.created_at),
  };
}

type Builder = ReturnType<ReturnType<typeof supabase.from>['select']>;

function applyContactQuery(query: Builder, orgId: string, q: ContactQuery, withEmployee = true): Builder {
  const groups: string[] = [];
  let b = query.eq('org_id', orgId);

  const archiveGroup = archiveQueryGroup('is_archived', q.archived, q.tab);
  if (archiveGroup) groups.push(archiveGroup);

  const { start, end } = resolveRange(q.range);
  if (start) b = b.gte('last_call_time', start.toISOString());
  if (end) b = b.lte('last_call_time', end.toISOString());

  switch (q.tab) {
    case 'answered':
      b = b.eq('last_call_type', 'incoming').gte('last_call_duration', 3);
      break;
    case 'not_answered':
      groups.push(
        'or(last_call_type.in.(missed,rejected,blocked),and(last_call_type.eq.incoming,last_call_duration.eq.0))',
      );
      break;
    case 'outgoing':
      b = b.eq('last_call_type', 'outgoing').gte('last_call_duration', 3);
      break;
    case 'outgoing_not_connected':
      b = b.eq('last_call_type', 'outgoing').eq('last_call_duration', 0);
      break;
    case 'never_connected':
      groups.push('or(total_connected.is.null,total_connected.eq.0)');
      break;
    case 'not_reviewed':
      b = b.eq('fully_reviewed', false);
      break;
    case 'no_note':
      groups.push('or(note.is.null,note.eq.)');
      break;
    case 'failed':
      b = b.gt('last_call_duration', 0).lt('last_call_duration', 3);
      break;
    default:
      break;
  }

  const search = q.search?.trim();
  if (search) {
    const digits = phoneQueryDigits(search);
    if (digits) b = b.ilike('phone', `%${digits}%`);
    else {
      const term = safeTerm(search);
      if (term) groups.push(`or(name.ilike.%${term}%,note.ilike.%${term}%,phone.ilike.%${term}%)`);
    }
  }

  if (q.connection === 'connected') b = b.gt('total_connected', 0);
  if (q.connection === 'never') groups.push('or(total_connected.is.null,total_connected.eq.0)');
  if (q.notes === 'with') b = b.not('note', 'is', null).neq('note', '');
  if (q.notes === 'without') groups.push('or(note.is.null,note.eq.)');
  if (q.reviewed === 'all_reviewed') b = b.eq('fully_reviewed', true);
  if (q.reviewed === 'pending') b = b.eq('fully_reviewed', false);
  if (q.recordings === 'with') b = b.gt('total_recordings', 0);
  if (q.recordings === 'without') groups.push('or(total_recordings.is.null,total_recordings.eq.0)');
  if (q.talkTime === 'short') b = b.lt('total_duration', 60);
  if (q.talkTime === 'medium') b = b.gte('total_duration', 60).lt('total_duration', 600);
  if (q.talkTime === 'long') b = b.gte('total_duration', 600);
  if (q.frequency === 'frequent') b = b.gt('total_calls', 10);
  if (q.frequency === 'rare') b = b.gte('total_calls', 1).lte('total_calls', 3);
  if (q.lastCallType) b = b.eq('last_call_type', q.lastCallType);
  if (q.label) {
    const label = q.label.trim();
    if (label) b = b.contains('display_labels', JSON.stringify([label]));
  }
  if (withEmployee && q.employeeId) b = b.eq('last_employee_id', Number(q.employeeId));

  if (groups.length === 1) b = b.or(groups[0].replace(/^or\((.*)\)$/s, '$1'));
  else if (groups.length > 1) b = b.or(`and(${groups.join(',')})`);
  return b;
}

// Keep the complete Contact model while leaving unused profile/address fields in the database.
const CONTACT_COLUMNS =
  'phone,name,display_name,note,display_note,label,display_labels,is_archived,is_starred,identity_id,last_call_time,last_call_type,last_call_duration,total_calls,total_connected,total_incoming,total_outgoing,total_missed,total_duration,total_recordings,unreviewed_count,last_call_rec_url,created_at';

const SORT_COLUMNS: Record<ContactSortKey, string> = {
  last_call_time: 'last_call_time',
  name: 'name',
  total_calls: 'total_calls',
  total_duration: 'total_duration',
};

// Activity mode builds contacts from every call in the range. Paging or sorting used to
// download all of those calls again; keep them for a minute per range and filters.
const ACTIVITY_CACHE_MS = 60_000;
const activityCalls = new Map<string, { at: number; calls: Promise<Awaited<ReturnType<typeof exportCalls>>> }>();
function cachedActivityCalls(orgId: string, q: ContactQuery) {
  const query = {
    range: q.range,
    tab: 'all' as const,
    employeeId: q.employeeId,
    archived: q.tab === 'excluded' ? ('only' as const) : q.archived,
  };
  const key = JSON.stringify([orgId, query]);
  const hit = activityCalls.get(key);
  if (hit && Date.now() - hit.at < ACTIVITY_CACHE_MS) return hit.calls;
  const calls = exportCalls(orgId, query);
  calls.catch(() => activityCalls.delete(key));
  activityCalls.set(key, { at: Date.now(), calls });
  return calls;
}

export async function listContacts(
  orgId: string,
  q: ContactQuery,
  page: number,
  pageSize: number,
  sort: Sort<ContactSortKey> = { key: 'last_call_time', dir: 'desc' },
  signal?: AbortSignal,
): Promise<Paged<Contact> & { warning?: string }> {
  if (q.activity) {
    const { contactActivity } = await import('../contact-activity');
    const calls = await cachedActivityCalls(orgId, q);
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const rows = contactActivity(calls, q, sort);
    return { rows: rows.slice((page - 1) * pageSize, page * pageSize), total: rows.length };
  }
  const from = (page - 1) * pageSize;
  const run = (withEmployee: boolean) => {
    const query = applyContactQuery(
      supabase.from('callers_view').select(CONTACT_COLUMNS, { count: 'exact' }),
      orgId,
      q,
      withEmployee,
    )
      .order(SORT_COLUMNS[sort.key] ?? 'last_call_time', { ascending: sort.dir === 'asc', nullsFirst: false })
      .order('phone', { ascending: true })
      .range(from, from + pageSize - 1);
    return signal ? query.abortSignal(signal) : query;
  };

  let res = await run(true);
  let warning: string | undefined;
  // Older databases don't expose last_employee_id on callers_view.
  if (res.error && q.employeeId && /last_employee_id/i.test(res.error.message)) {
    res = await run(false);
    warning = 'Filtering contacts by employee isn’t available on this workspace yet — showing everyone.';
  }
  const rows = unwrap(res) as Record<string, unknown>[];
  return { rows: (rows ?? []).map(mapContact), total: res.count ?? 0, warning };
}

export async function exportContacts(orgId: string, q: ContactQuery): Promise<Contact[]> {
  if (q.activity) {
    const { contactActivity } = await import('../contact-activity');
    return contactActivity(
      await exportCalls(orgId, {
        range: q.range,
        tab: 'all',
        employeeId: q.employeeId,
        archived: q.tab === 'excluded' ? 'only' : q.archived,
      }),
      q,
      { key: 'total_calls', dir: 'desc' },
    );
  }
  const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
    applyContactQuery(supabase.from('callers_view').select(CONTACT_COLUMNS), orgId, q)
      .order('last_call_time', { ascending: false, nullsFirst: false })
      .order('phone', { ascending: true })
      .range(from, to),
  );
  return rows.map(mapContact);
}

export async function countContacts(
  orgId: string,
  range: DateRangeValue,
  employeeId?: string,
): Promise<TabCounts> {
  const { start, end } = resolveRange(range);
  const data = unwrap(
    await supabase.rpc('get_caller_counts', {
      p_org_id: orgId,
      p_start_date: start ? start.toISOString() : null,
      p_end_date: end ? end.toISOString() : null,
      p_employee_id: employeeId ? Number(employeeId) : null,
    }),
  ) as Record<string, unknown> | null;
  const n = (k: string) => toNum(data?.[k]);
  return {
    all: n('all'),
    answered: n('answered'),
    not_answered: n('not_answered'),
    outgoing: n('outgoing'),
    outgoing_not_connected: n('outgoing_not_connected'),
    never_connected: n('never_connected'),
    not_reviewed: n('not_reviewed'),
    no_note: n('no_note'),
    failed: n('failed'),
    excluded: n('excluded'),
  };
}

export async function getContact(orgId: string, phone: string): Promise<Contact | null> {
  const row = unwrap(
    await supabase
      .from('callers_view')
      .select(CONTACT_COLUMNS)
      .eq('org_id', orgId)
      .eq('phone', phone)
      .maybeSingle(),
  ) as Record<string, unknown> | null;
  return row ? mapContact(row) : null;
}

export interface IdentityPatch {
  name?: string;
  note?: string;
  labels?: string[];
  isStarred?: boolean;
  isArchived?: boolean;
}

/**
 * Names, person notes, labels, stars and archive state live on `identities`,
 * one row per (org, phone). Upsert it, make sure the caller row points at it,
 * then let webhook subscribers know.
 */
export async function saveIdentity(orgId: string, phone: string, patch: IdentityPatch): Promise<void> {
  const row: Record<string, unknown> = { org_id: orgId, phone, updated_at: new Date().toISOString() };
  if (patch.name !== undefined) row.name = patch.name.trim() || null;
  if (patch.note !== undefined) row.note = patch.note.trim();
  if (patch.labels !== undefined) row.labels = parseLabels(patch.labels);
  if (patch.isStarred !== undefined) row.is_starred = patch.isStarred;
  if (patch.isArchived !== undefined) row.is_archived = patch.isArchived;

  const saved = unwrap(
    await supabase.from('identities').upsert(row, { onConflict: 'org_id,phone' }).select('id').single(),
  ) as { id: number };

  unwrap(
    await supabase.from('callers').update({ identity_id: saved.id }).eq('org_id', orgId).eq('phone', phone),
  );

  if (patch.name !== undefined || patch.note !== undefined || patch.labels !== undefined) {
    // Fire-and-forget: webhook delivery must never block or fail an edit.
    apiPost('/api/webhooks/caller-updated', { phone }).catch(() => undefined);
  }
}

/** Permanently remove every call, recording and saved detail for one number. */
export async function deleteContactData(orgId: string, phone: string): Promise<void> {
  const recordings = await fetchAllRows<{ recording_url: string | null }>((from, to) =>
    supabase
      .from('call_log')
      .select('recording_url')
      .eq('org_id', orgId)
      .eq('caller_phone', phone)
      .not('recording_url', 'is', null)
      .range(from, to),
  );
  await deleteRecordingFiles(recordings.map((r) => r.recording_url));

  unwrap(await supabase.from('call_log').delete().eq('org_id', orgId).eq('caller_phone', phone));
  unwrap(await supabase.from('callers').delete().eq('org_id', orgId).eq('phone', phone));

  const identity = await supabase.from('identities').delete().eq('org_id', orgId).eq('phone', phone);
  if (identity.error) {
    // Still referenced elsewhere (e.g. CRM) — clear the personal details instead.
    unwrap(
      await supabase
        .from('identities')
        .update({ name: null, note: '', labels: [], is_starred: false, updated_at: new Date().toISOString() })
        .eq('org_id', orgId)
        .eq('phone', phone),
    );
  }
}
